'use client';

import { scrollIntoView } from '@react-aria/utils';
import clsx from 'clsx';
import {
  Children,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Table as AriaTable,
  ResizableTableContainer,
  TableBody,
  type SortDescriptor,
} from 'react-aria-components';
import styles from './DataTable.module.scss';
import { DataTableHeader } from './Header';
import { BetaDataTableRowLoader } from './RowLoader';
import type { BetaColumnHeader } from './types';
import { StickyColumnsContext, useStickyColumns } from './useStickyColumns';

const defaultLoaderRowCount = 5;

const ARROW_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

// Scrolls the focused element into view after an arrow-key navigation.
//
// requestAnimationFrame defers until after React Aria has committed its
// focus() call so document.activeElement is the newly-focused cell, not
// the previous one.
//
// scrollIntoView from @react-aria/utils scrolls only within the given
// scroll container (using offsetLeft/offsetTop traversal) and never
// touches any ancestor above it. This avoids the browser's built-in
// scrollIntoView algorithm being confused by the overflow:hidden
// scroll-shadow-wrapper sitting above the table's scroll container.
function scrollFocusedCellIntoView(scrollContainer: HTMLElement | null): void {
  const activeEl = document.activeElement as HTMLElement | null;
  if (!activeEl || !scrollContainer) return;
  scrollIntoView(scrollContainer, activeEl);
}

export type { BetaColumnHeader } from './types';

export type BetaDataTableProps = {
  /** Accessibility label for the table */
  ariaLabel: string;
  /** Component to render for bulk actions */
  bulkActionsComponent?: ReactNode;
  /** Table row children */
  children?: ReactNode;
  /** Additional CSS class name */
  className?: string;
  /** Column definitions */
  columns: BetaColumnHeader[];
  /** Whether the table is in a loading state */
  isLoading?: boolean;
  /** Callback when column resize ends */
  onResizeEnd?: () => void;
  /** Callback when sort changes */
  onSortChange?: (sortDescriptor: SortDescriptor) => void;
  /** Custom row loader component */
  rowLoader?: ReactNode;
  /** Whether to show selection checkboxes */
  showCheckboxes?: boolean;
  /** Current sort state */
  sortDescriptor?: SortDescriptor;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Minimum table width in pixels */
  minWidth?: number;
  /** Tooltip to show on sortable columns when sorting is disabled */
  sortDisabledTooltip?: string;
};

/**
 * The root table component. Renders a resizable, sortable data table built on
 * React Aria Components. Use within a `BetaDataTableProvider` for row selection.
 */
export const BetaDataTableRoot = ({
  ariaLabel,
  bulkActionsComponent,
  children,
  className,
  columns,
  isLoading,
  onResizeEnd,
  onSortChange,
  rowLoader,
  showCheckboxes,
  sortDescriptor: _sortDescriptor,
  style,
  minWidth,
  sortDisabledTooltip,
}: BetaDataTableProps) => {
  // Callback refs (state + useCallback) instead of useRef: mutating
  // ref.current doesn't trigger a re-render, so useStickyColumns would never
  // see the DOM node in its dependency arrays. useState + useCallback ensures
  // a re-render fires once the element mounts, letting the hook measure it.
  const [tableEl, setTableEl] = useState<HTMLTableElement | null>(null);
  const tableCallbackRef = useCallback((node: HTMLTableElement | null) => {
    setTableEl(node);
  }, []);

  const [scrollContainerEl, setScrollContainerEl] =
    useState<HTMLDivElement | null>(null);
  const scrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    setScrollContainerEl(node);
  }, []);

  const rafHandleRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (rafHandleRef.current !== null)
        cancelAnimationFrame(rafHandleRef.current);
    },
    []
  );

  const sortDescriptor = _sortDescriptor ?? {
    direction: 'ascending',
    column: '',
  };

  const onSort = onSortChange
    ? (descriptor: SortDescriptor) => {
        onSortChange(descriptor);
      }
    : undefined;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (ARROW_KEYS.has(e.key)) {
        if (rafHandleRef.current !== null)
          cancelAnimationFrame(rafHandleRef.current);
        rafHandleRef.current = requestAnimationFrame(() => {
          rafHandleRef.current = null;
          scrollFocusedCellIntoView(scrollContainerEl);
        });
      }
    },
    [scrollContainerEl]
  );

  const tableStyle = {
    ...style,
    minWidth: minWidth ? `${minWidth}px` : undefined,
  };

  const rowLoaderComponent = rowLoader ?? (
    <BetaDataTableRowLoader
      columns={columns.length}
      rows={Children.count(children) || defaultLoaderRowCount}
      showCheckboxes={showCheckboxes}
    />
  );

  const stickyColumnsValue = useStickyColumns({
    columns,
    showCheckboxes: !!showCheckboxes,
    table: tableEl,
    scrollContainer: scrollContainerEl,
  });

  return (
    <StickyColumnsContext.Provider value={stickyColumnsValue}>
      <div
        className={styles.scrollShadowWrapper}
        data-can-scroll-left={
          !stickyColumnsValue.hasStickyLeft && stickyColumnsValue.canScrollLeft
            ? ''
            : undefined
        }
        data-can-scroll-right={
          !stickyColumnsValue.hasStickyRight &&
          stickyColumnsValue.canScrollRight
            ? ''
            : undefined
        }
        onKeyDown={handleKeyDown}
      >
        <ResizableTableContainer
          ref={scrollContainerRef}
          className={styles.tableContainer}
          onResizeEnd={onResizeEnd}
        >
          <AriaTable
            ref={tableCallbackRef}
            aria-label={ariaLabel}
            // selectionMode="multiple" enables React Aria's row selection, but we
            // only use checkbox-based selection — never click-to-select rows since we want to allow clickable cell content.
            // React Aria disables text selection on rows during press interactions to
            // prevent accidental highlights; we override that in the stylesheet so
            // users can still select and copy cell content.
            selectionMode="multiple"
            className={clsx(styles.table, className)}
            onSortChange={onSort}
            sortDescriptor={sortDescriptor}
            style={tableStyle}
            data-zds
          >
            <DataTableHeader
              bulkActionsComponent={bulkActionsComponent}
              isLoading={isLoading}
              showCheckboxes={showCheckboxes}
              columns={columns}
              sortDisabledTooltip={sortDisabledTooltip}
            />
            <TableBody>{isLoading ? rowLoaderComponent : children}</TableBody>
          </AriaTable>
        </ResizableTableContainer>
      </div>
    </StickyColumnsContext.Provider>
  );
};

export { BetaDataTableProvider } from './Context';
export { BetaDataTableRow } from './Row';
export { BetaDataTableRowLoader } from './RowLoader';
